import { Component, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroupDirective,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

// Importaciones de entornos.
import { environment } from '../../../../environments/environment';

// Importaciones de los servicios.
import { LogService } from '../../../services/log.service';
import { MeService } from '../../../services/me.service';

// Importaciones de los validadores.
import { passwordMatchValidator } from '../../../validators/password-match.validator';

// Importaciones de modelos.
import { Log } from '../../../models/log.model';

/**
 * Componente para la gestión y actualización de la contraseña del usuario.
 *
 * Permite cambiar la contraseña mediante un formulario con validaciones
 * y muestra notificaciones sobre el resultado del proceso.
 */
@Component({
  selector: 'app-password-settings',
  templateUrl: './password-settings.component.html',
  standalone: false,
})
export class PasswordSettingsComponent {
  // Permite reiniciar también el estado de envío del formulario.
  @ViewChild(FormGroupDirective) formDirective!: FormGroupDirective;

  /**
   * Indica si la contraseña actual debe estar oculta en el campo de entrada.
   */
  currentPasswordHide: boolean = true;

  /**
   * Indica si la nueva contraseña debe estar oculta en el campo de entrada.
   */
  newPasswordHide: boolean = true;

  /**
   * Indica si la confirmación de la nueva contraseña debe estar oculta en el campo de entrada.
   */
  confirmNewPasswordHide: boolean = true;

  /**
   * Indica si la operación de actualizar la contraseña está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se actualiza la contraseña del empleado.
   */
  loading: boolean = false;

  /**
   * Lista de errores al actualizar la contraseña.
   *
   * Contiene los mensajes de error generados si la operación de guardado falla.
   */
  errors: string[] = [];

  /**
   * Formulario de actualización de contraseña con validaciones.
   */
  form: FormGroup = new FormGroup(
    {
      currentPassword: new FormControl('', Validators.required),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(
          '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+{}:;<>,.?~/-]).{8,}$',
        ),
      ]),
      confirmNewPassword: new FormControl('', Validators.required),
    },
    { validators: passwordMatchValidator('newPassword', 'confirmNewPassword') },
  );

  /**
   * Obtiene el control del formulario correspondiente a la contraseña actual.
   *
   * @returns El control de `currentPassword` o `null` si no existe.
   */
  get formCurrentPassword(): AbstractControl | null {
    return this.form.get('currentPassword');
  }

  /**
   * Obtiene el control del formulario correspondiente a la nueva contraseña.
   *
   * @returns El control de `newPassword` o `null` si no existe.
   */
  get formNewPassword(): AbstractControl | null {
    return this.form.get('newPassword');
  }

  /**
   * Obtiene el control del formulario correspondiente a la confirmación de la nueva contraseña.
   *
   * @returns El control de `confirmNewPassword` o `null` si no existe.
   */
  get formConfirmNewPassword(): AbstractControl | null {
    return this.form.get('confirmNewPassword');
  }

  /**
   * Constructor de la clase.
   *
   * @param logService - Servicio para gestionar los registros.
   * @param meService - Servicio para gestionar la información del usuario.
   * @param snackBar - Servicio de Angular Material para mostrar notificaciones emergentes.
   */
  constructor(
    private logService: LogService,
    private meService: MeService,
    private snackBar: MatSnackBar,
  ) {}

  /**
   * Procesa la actualización de la contraseña del usuario,
   * aplicando validaciones y mostrando notificaciones según el resultado.
   */
  async updatePassword(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    this.loading = true;
    this.errors = [];

    try {
      const meResponse: string = await this.meService.patchPassword(
        this.formCurrentPassword?.value,
        this.formNewPassword?.value,
      );

      // Añadir un registro de cambio de contraseña.
      try {
        await this.logService.create(
          new Log({
            description: 'Ha cambiado la contraseña.',
          }),
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de cambio de contraseña.',
          );
        }
      }

      this.snackBar.open(meResponse, '', {
        duration: 5000,
      });

      this.formDirective.resetForm();

      this.currentPasswordHide = true;
      this.newPasswordHide = true;
      this.confirmNewPasswordHide = true;
    } catch (errors: any) {
      this.errors = errors;
    } finally {
      this.loading = false;
    }
  }
}
